import { computed, ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { showNotify } from 'vant'
import type { DepositType } from '@/type/Appointment'

/**
 * บริการที่สนใจของฟอร์มนัดหมาย (LMP-7840) - รายการมาจากการตั้งค่าราย LINE OA ที่ POS (repo.fetchServices) ไม่ใช่ค่าตายตัว
 * เลือกได้ 1 บริการและบังคับเลือกตอนสร้างนัด - หัตถการที่เลือกได้ขึ้นกับบริการ ดู useAppointmentDepositTypeWithInterestTag
 *
 * ห้ามเลือกบริการให้เองตอนเริ่ม / ล้างฟอร์ม (Xpress เคยเลือก "สนใจซื้อคอร์ส" ให้ - ยกเลิกไปใน LMP-7809)
 */
export interface useDepositTypesInterface {
  depositTypes: Ref<DepositType[]>
  // อ่านอย่างเดียว - เลือกผ่าน onSelectDepositType (กัน id ที่ไม่อยู่ในรายการ) ห้าม v-model ตรง
  selectedDepositTypeId: Readonly<Ref<number | null>>
  selectedDepositType: ComputedRef<DepositType | null>
  initializeDepositTypes: (list: DepositType[]) => void
  onSelectDepositType: (id: number) => void
  validateRequireSelectDepositType: (t: (key: string) => string) => boolean
  getSelectedDepositTypeNames: () => string[]
  resetDepositTypes: () => void
}

/**
 * ชื่อบริการตามภาษาของฟอร์ม - backend fallback คำแปลให้แล้ว แต่กันค่าว่างไว้อีกชั้นแบบ getBranchDisplayNamei18n
 * (ไม่ใช้ชื่อนี้ใน note - note ใช้ชื่อไทยเสมอ ดู getSelectedDepositTypeNames)
 */
export function getDepositTypeDisplayName(depositType: DepositType, lang: string): string {
  const th = depositType.name
  const en = depositType.name_en || th
  const cn = depositType.name_cn || en

  if (lang === 'cn') return cn
  if (lang === 'en') return en
  return th
}

export function useDepositTypes(): useDepositTypesInterface {
  const depositTypes = ref<DepositType[]>([])
  const selectedDepositTypeId = ref<number | null>(null)

  const selectedDepositType = computed(
    () => depositTypes.value.find(depositType => depositType.id === selectedDepositTypeId.value) ?? null,
  )

  /**
   * ตั้งรายการบริการ (ลำดับตามที่ backend ส่งมา) - บริการที่เลือกไว้ไม่อยู่ในรายการใหม่แล้ว (ถูกปิด / ถอดที่ POS) = ล้างที่เลือก
   */
  function initializeDepositTypes(list: DepositType[]) {
    depositTypes.value = list
    if (!list.some(depositType => depositType.id === selectedDepositTypeId.value)) {
      selectedDepositTypeId.value = null
    }
  }

  /**
   * เลือกได้ทีละ 1 - กดตัวที่เลือกอยู่ซ้ำยังเลือกอยู่ (บังคับเลือก ไม่มีทางยกเลิกให้ว่าง)
   */
  function onSelectDepositType(id: number) {
    if (!depositTypes.value.some(depositType => depositType.id === id)) return
    selectedDepositTypeId.value = id
  }

  function validateRequireSelectDepositType(t: (key: string) => string): boolean {
    if (selectedDepositType.value) return true
    showNotify({
      type: 'danger',
      message: t('appointmentrequest.deposit_type.error'),
    })
    return false
  }

  /**
   * ชื่อไทยของบริการที่เลือกสำหรับต่อท้าย note (createSubmitNote) - คลินิกอ่าน note จึงไม่ใช้ชื่อตามภาษาของฟอร์ม
   * ใช้ที่นี่ที่เดียวทั้ง ABC และ Xpress; คืนเป็น array ตามรูปแบบที่ createSubmitNote รับ
   */
  function getSelectedDepositTypeNames(): string[] {
    return selectedDepositType.value ? [selectedDepositType.value.name] : []
  }

  function resetDepositTypes() {
    selectedDepositTypeId.value = null
  }

  return {
    depositTypes,
    selectedDepositTypeId,
    selectedDepositType,
    initializeDepositTypes,
    onSelectDepositType,
    validateRequireSelectDepositType,
    getSelectedDepositTypeNames,
    resetDepositTypes,
  }
}
